import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks';
import {
  getEligibilityRules,
  updateEligibilityRules,
  getCreditLevelConfig,
  updateCreditLevelConfig,
  getInterestRateConfig,
  updateInterestRateConfig,
  getExposureLimits,
  updateExposureLimits,
  getConfig,
  updateConfig,
  getScoreConfig,
} from '@/services/credit-engine.service';
import type {
  EligibilityRuleItem,
  CreditLevelItem,
  InterestRateItem,
  ExposureLimitItem,
} from '@/types/policy.types';
import { PARTNER_ID } from '@/constants';

type OperationalParamsItem = Record<string, unknown> & {
  opening_fee_pct?: number;
  opening_fee_financed?: boolean;
};

export interface OpeningFeeDraft {
  rate: number;
  is_financed: boolean;
}

const QUERY_KEYS = {
  eligibility: ['policies', 'eligibility', PARTNER_ID],
  creditLevels: ['policies', 'credit-levels', PARTNER_ID],
  scores: ['policies', 'scores', PARTNER_ID],
  interestRates: ['policies', 'interest-rates', PARTNER_ID],
  operationalParams: ['policies', 'operational-params', PARTNER_ID],
  exposureLimits: ['policies', 'exposure-limits', PARTNER_ID],
} as const;

export function usePolicies() {
  const queryClient = useQueryClient();
  const toast = useToast();

  // Each query is independent — a failure in one tab should not block the others.
  // Queries that may fail (missing tables, IAM) catch errors and return empty data.
  const safeQuery = <T,>(fn: () => Promise<T>): (() => Promise<T>) =>
    () => fn().catch((err) => {
      console.warn('[usePolicies] Query failed gracefully:', err?.response?.status, err?.response?.data?.msg ?? err?.message);
      return { data: null } as T;
    });

  const eligibilityQuery = useQuery({
    queryKey: QUERY_KEYS.eligibility,
    queryFn: safeQuery(() => getEligibilityRules(PARTNER_ID)),
  });

  const creditLevelsQuery = useQuery({
    queryKey: QUERY_KEYS.creditLevels,
    queryFn: () => getCreditLevelConfig(PARTNER_ID),
  });

  const interestRatesQuery = useQuery({
    queryKey: QUERY_KEYS.interestRates,
    queryFn: () => getInterestRateConfig(PARTNER_ID),
  });

  const operationalParamsQuery = useQuery({
    queryKey: QUERY_KEYS.operationalParams,
    queryFn: safeQuery(() => getConfig('operational-params', PARTNER_ID)),
  });

  const scoresQuery = useQuery({
    queryKey: QUERY_KEYS.scores,
    queryFn: safeQuery(() => getScoreConfig(PARTNER_ID)),
  });

  const exposureLimitsQuery = useQuery({
    queryKey: QUERY_KEYS.exposureLimits,
    queryFn: safeQuery(() => getExposureLimits(PARTNER_ID)),
  });

  // ─── Mutations ────────────────────────────────────────────────────────────

  // Strip any Dynamoose Document / React Query proxy references — send pure JSON
  const clean = <T,>(items: T[]): T[] => JSON.parse(JSON.stringify(items));

  const eligibilityMutation = useMutation({
    mutationFn: (items: EligibilityRuleItem[]) =>
      updateEligibilityRules({ partnerId: PARTNER_ID, items: clean(items) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.eligibility });
      toast.success('Reglas de elegibilidad actualizadas');
    },
    onError: () => {
      toast.error('Error al actualizar reglas de elegibilidad');
    },
  });

  const creditLevelsMutation = useMutation({
    mutationFn: (items: CreditLevelItem[]) =>
      updateCreditLevelConfig({ partnerId: PARTNER_ID, items: clean(items) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.creditLevels });
      toast.success('Niveles de credito actualizados');
    },
    onError: (err: any) => {
      console.error('CreditLevels update error:', err?.response?.data ?? err);
      const msg = err?.response?.data?.msg || err?.message || 'Error desconocido';
      toast.error(`Error al actualizar niveles: ${msg}`);
    },
  });

  const interestRatesMutation = useMutation({
    mutationFn: (items: InterestRateItem[]) =>
      updateInterestRateConfig({ partnerId: PARTNER_ID, items: clean(items) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.interestRates });
      toast.success('Tasas de interes actualizadas');
    },
    onError: () => {
      toast.error('Error al actualizar tasas de interes');
    },
  });

  // La comision de apertura vive dentro de `operational-params`. El backend
  // valida shape strict (5 numeros + 1 bool), asi que mergeamos sobre el item
  // actual y reenviamos el objeto completo.
  const operationalFeeMutation = useMutation({
    mutationFn: (fee: OpeningFeeDraft) => {
      const raw = operationalParamsQuery.data?.data;
      const current = (Array.isArray(raw) && raw.length > 0 ? raw[0] : {}) as OperationalParamsItem;
      const merged = clean([{
        ...current,
        opening_fee_pct: fee.rate,
        opening_fee_financed: fee.is_financed,
      }]);
      return updateConfig('operational-params', { partnerId: PARTNER_ID, items: merged });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.operationalParams });
      void queryClient.invalidateQueries({ queryKey: ['config', 'operational-params'] });
      toast.success('Comision de apertura actualizada');
    },
    onError: () => {
      toast.error('Error al actualizar comision de apertura');
    },
  });

  const scoresMutation = useMutation({
    mutationFn: (items: any[]) =>
      updateConfig('scores', { partnerId: PARTNER_ID, items: clean(items) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.scores });
      toast.success('Configuracion de scoring actualizada');
    },
    onError: () => {
      toast.error('Error al actualizar configuracion de scoring');
    },
  });

  const exposureLimitsMutation = useMutation({
    mutationFn: (items: ExposureLimitItem[]) =>
      updateExposureLimits({ partnerId: PARTNER_ID, items: clean(items) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.exposureLimits });
      toast.success('Limites de exposicion actualizados');
    },
    onError: () => {
      toast.error('Error al actualizar limites de exposicion');
    },
  });

  // ─── Aggregated state ─────────────────────────────────────────────────────
  // Only block the page on queries we know should work (creditLevels, interestRates).
  // Eligibility, scores, and exposureLimits may fail gracefully (missing tables/IAM).

  const isLoading =
    creditLevelsQuery.isLoading ||
    interestRatesQuery.isLoading;

  const isError =
    creditLevelsQuery.isError ||
    interestRatesQuery.isError;

  const error =
    creditLevelsQuery.error ??
    interestRatesQuery.error;

  function refetchAll() {
    void eligibilityQuery.refetch();
    void creditLevelsQuery.refetch();
    void scoresQuery.refetch();
    void interestRatesQuery.refetch();
    void operationalParamsQuery.refetch();
    void exposureLimitsQuery.refetch();
  }

  // Proyectamos solo los campos de comision para que la seccion no necesite
  // conocer el shape completo de operational-params.
  const operationalParamsRaw = operationalParamsQuery.data?.data;
  const operationalParamsItem = (Array.isArray(operationalParamsRaw) && operationalParamsRaw.length > 0
    ? operationalParamsRaw[0]
    : null) as OperationalParamsItem | null;
  const openingFee: OpeningFeeDraft | null = operationalParamsItem
    ? {
        rate: operationalParamsItem.opening_fee_pct ?? 0,
        is_financed: operationalParamsItem.opening_fee_financed ?? true,
      }
    : null;

  return {
    // Data — arrays of raw DynamoDB items
    eligibility: eligibilityQuery.data?.data ?? null,
    creditLevels: creditLevelsQuery.data?.data ?? null,
    scores: scoresQuery.data?.data ?? null,
    interestRates: interestRatesQuery.data?.data ?? null,
    openingFee,
    exposureLimits: exposureLimitsQuery.data?.data ?? null,

    // Mutations
    updateEligibility: eligibilityMutation.mutate,
    updateCreditLevels: creditLevelsMutation.mutate,
    updateScores: scoresMutation.mutate,
    updateInterestRates: interestRatesMutation.mutate,
    updateOpeningFee: operationalFeeMutation.mutate,
    updateExposureLimits: exposureLimitsMutation.mutate,

    // Mutation loading states
    isSavingEligibility: eligibilityMutation.isPending,
    isSavingCreditLevels: creditLevelsMutation.isPending,
    isSavingScores: scoresMutation.isPending,
    isSavingInterestRates: interestRatesMutation.isPending,
    isSavingOpeningFee: operationalFeeMutation.isPending,
    isSavingExposureLimits: exposureLimitsMutation.isPending,

    // Aggregated state
    isLoading,
    isError,
    error,
    refetchAll,
  };
}
